import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Col, Row } from '../../lib/utils';
import { Body2Bold, Body3Regular, CaptionRegular } from '../../lib/font';
import { colors } from '../../lib/colors';
import { getPaymentRequest } from './data/dummyData';
import PaymentHeader from './components/PaymentHeader';
import TicketInfoCard from './components/TicketInfoCard';
import SellerInfoSection from './components/SellerInfoSection';
import RefundPolicySection from './components/RefundPolicySection';
import AgreementCheckbox from './components/AgreementCheckbox';
import StickyBottomButton from './components/StickyBottomButton';

const TOSS_CLIENT_KEY = 'test_ck_ma60RZblrqyzKPxa4bBZrwzYWBn1';

const MemberPaymentPage: React.FC = () => {
    const { paymentRequestId } = useParams<{ paymentRequestId: string }>();
    const navigate = useNavigate();
    const [isAgreed, setIsAgreed] = useState(false);
    const [isRequestingPayment, setIsRequestingPayment] = useState(false);

    const data = getPaymentRequest(paymentRequestId || '');

    if (!data) {
        return (
            <Col style={{ width: '100%', minHeight: '100vh', alignItems: 'center', justifyContent: 'center', backgroundColor: '#F5F7FA' }}>
                <Col
                    style={{
                        width: '100%',
                        maxWidth: 480,
                        backgroundColor: '#FFFFFF',
                        borderRadius: 16,
                        padding: '32px 24px',
                        alignItems: 'center',
                        margin: 16,
                        boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
                    }}
                >
                    <Body2Bold style={{ color: colors.ERROR }}>요청을 찾을 수 없습니다</Body2Bold>
                    <Body3Regular style={{ color: colors.LAVEL_2, marginTop: 8, textAlign: 'center' }}>
                        유효하지 않은 결제 요청입니다. 링크를 다시 확인해 주세요.
                    </Body3Regular>
                </Col>
            </Col>
        );
    }

    const isPrepay = data.paymentType === 'PREPAY';

    const loadTossScript = () =>
        new Promise<void>((resolve, reject) => {
            if ((window as any).TossPayments) {
                resolve();
                return;
            }

            const existScript = document.querySelector('script[data-toss-sdk="true"]') as HTMLScriptElement | null;
            if (existScript) {
                existScript.addEventListener('load', () => resolve(), { once: true });
                existScript.addEventListener('error', () => reject(new Error('토스 SDK 로드 실패')), { once: true });
                return;
            }

            const script = document.createElement('script');
            script.src = 'https://js.tosspayments.com/v2/standard';
            script.async = true;
            script.dataset.tossSdk = 'true';
            script.onload = () => resolve();
            script.onerror = () => reject(new Error('토스 SDK 로드 실패'));
            document.head.appendChild(script);
        });

    const requestTossPayment = async () => {
        if (!data || !paymentRequestId || isRequestingPayment) return;

        setIsRequestingPayment(true);
        try {
            await loadTossScript();

            if (!(window as any).TossPayments) {
                throw new Error('토스 SDK 초기화 실패');
            }

            const tossPayments = (window as any).TossPayments(TOSS_CLIENT_KEY);
            const payment = (tossPayments as any).payment({
                customerKey: `member-${paymentRequestId}`,
            });

            await payment.requestPayment({
                method: 'CARD',
                amount: {
                    currency: 'KRW',
                    value: 50//data.totalPrice,
                },
                orderId: `${paymentRequestId}-${Date.now()}`,
                orderName: data.ticketName,
                successUrl: `${window.location.origin}/pay/complete?requestId=${paymentRequestId}`,
                failUrl: `${window.location.origin}/pay/fail?requestId=${paymentRequestId}`,
                customerName: data.memberName,
                customerMobilePhone: '01012341234',
                card: {
                    useEscrow: false,
                    flowMode: 'DEFAULT',
                    useCardPoint: false,
                    useAppCardOnly: false,
                },
            });
        } catch (error) {
            console.log(error);
            setIsRequestingPayment(false);
        }
    };

    const handleNext = () => {
        if (!isAgreed || isRequestingPayment) return;
        if (isPrepay) {
            requestTossPayment();
        } else {
            navigate(`/pay/${paymentRequestId}/card-register`);
        }
    };

    return (
        <Col style={{ width: '100%', minHeight: '100vh', alignItems: 'center', backgroundColor: '#FFFFFF' }}>
            <Col style={{ width: '100%', maxWidth: 480, minHeight: '100vh', backgroundColor: '#FFFFFF' }}>
                <PaymentHeader title="수강권 결제" />

                <Col style={{ flex: 1, padding: '0 16px', paddingBottom: 80, gap: 12 }}>
                    {/* 트레이너 결제 요청 배너 */}
                    <Row
                        style={{
                            width: '100%',
                            padding: '16px 20px',
                            backgroundColor: '#EFF6FF',
                            borderRadius: 12,
                            alignItems: 'center',
                            gap: 12,
                            marginTop: 16,
                        }}
                    >
                        <div
                            style={{
                                width: 40,
                                height: 40,
                                borderRadius: 20,
                                backgroundColor: colors.primary,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0,
                            }}
                        >
                            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                                <path d="M10 10a4 4 0 100-8 4 4 0 000 8zm0 2c-4 0-7 2-7 4v1h14v-1c0-2-3-4-7-4z" fill="#FFFFFF" />
                            </svg>
                        </div>
                        <Col style={{ flex: 1 }}>
                            <Body3Regular style={{ color: '#1E40AF', fontWeight: 600 }}>
                                {data.centerName} {data.trainerName}님이 수강권 결제를 요청했어요
                            </Body3Regular>
                        </Col>
                    </Row>

                    {/* 결제 방식 배지 */}
                    <Row style={{ gap: 8, marginTop: 4 }}>
                        <div
                            style={{
                                padding: '6px 14px',
                                borderRadius: 20,
                                backgroundColor: isPrepay ? '#E8F5E9' : '#FFF3E0',
                                display: 'inline-flex',
                            }}
                        >
                            <CaptionRegular style={{ color: isPrepay ? '#2E7D32' : '#E65100', fontWeight: 600 }}>
                                {isPrepay ? '선결제' : '수업마다 결제'}
                            </CaptionRegular>
                        </div>
                    </Row>

                    {/* 수강권 정보 카드 */}
                    <TicketInfoCard data={data} />

                    {/* 환불/취소/유의사항 */}
                    <RefundPolicySection />

                    {/* 판매자 정보 */}
                    <SellerInfoSection data={data} />

                    {/* 동의 체크 */}
                    <div style={{ marginTop: 4 }}>
                        <AgreementCheckbox checked={isAgreed} onChange={setIsAgreed} />
                    </div>
                </Col>

                {/* 하단 고정 버튼 */}
                <StickyBottomButton
                    text={isPrepay ? (isRequestingPayment ? '결제창 준비중...' : '결제하기') : '카드 등록으로'}
                    onClick={handleNext}
                    disabled={!isAgreed || isRequestingPayment}
                />
            </Col>
        </Col>
    );
};

export default MemberPaymentPage;
